import React, {useState} from 'react';
import {Button, Card, Flex, Form, message, Upload, UploadFile} from "antd";
import ButtonBlack from "../../../components/ui/buttons/ButtonBlack";
import Title from "antd/es/typography/Title";
import TextArea from "antd/lib/input/TextArea";
import {api} from "../../../api/apiProvider";
import urls from "../../../api/urls";
import {useParams} from "react-router-dom";
import SwitchGreen from "../../ui/inputs/SwitchGreen";
import IconUploadFile from "../../ui/icons/IconUploadFile";
import {useTranslation} from "react-i18next";


interface EngineProps {
  updateData: Function,
}

const addEngine = async (data: any) => {
  return await api.post(urls.engines, {arg: data})
};

export default function Engine({updateData}: EngineProps) {
  const {t} = useTranslation();

  const routeParams = useParams();
  const [isUploadByFile, setIsUploadByFile] = useState<boolean>(false);
  const briefId = Number(routeParams.id) || 0;
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isValuesChanged, setIsValuesChanged] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [form] = Form.useForm();

  const isValidJSON = (str: string) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  const onSave = async (values: any) => {
    if (!isUploadByFile && !isValidJSON(values.json)) {
      message.error(t('factors.jsonNotValid'));
      return
    }

    if (isUploadByFile && !values.file) {
      message.error(t('factors.emptyFile'));
      return
    }

    setIsLoading(true);

    const data = {
      brief: `/api${urls.briefs}/${briefId}`,
      json: values.json,
    };

    if (isUploadByFile) {
      data.json = await getJsonFromFile();
    }

    addEngine(data).then(response => {
      if (response && response['@id'] && response.id) {
        message.success(t('common.saved'));
        updateData();
        setIsValuesChanged(false)
      }
      setIsLoading(false)
    }).catch(errors => {
      if (errors['hydra:description']) {
        message.error(errors['hydra:description'])
      }
      setIsLoading(false)
    })
  };

  const getJsonFromFile = async (): Promise<any> => {
    if (!fileList || fileList.length === 0) {
      return null;
    }

    const firstFile = fileList[0];

    if (firstFile instanceof File || firstFile.originFileObj instanceof File) {
      const file = firstFile instanceof File ? firstFile : firstFile.originFileObj;
      const reader = new FileReader();

      return new Promise<any>((resolve, reject) => {
        reader.onload = (event) => {
          try {
            const result = event.target?.result;
            if (typeof result === 'string') {
              resolve(result);
            } else {
              reject(new Error(t('factors.fileReadError')));
            }
          } catch (error) {
            reject(new Error(t('factors.fileReadError')));
          }
        };

        reader.onerror = () => {
          reject(new Error(t('factors.fileReadError')));
        };

        reader.readAsText(file as File);
      });
    }

    return null;
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  return (
    <Card>
      <Flex vertical gap={'middle'}>
        <Flex justify={'space-between'}>
          <Title level={5}>{t('factors.uploadFile')}</Title>
          <ButtonBlack
            onClick={() => {
              form.submit()
            }}
            disabled={!isValuesChanged}
            loading={isLoading}
          >{t('common.save')}</ButtonBlack>
        </Flex>

        <Form
          form={form}
          name="engine"
          onFinish={onSave}
          layout="vertical"
          autoComplete="off"
          onValuesChange={() => {
            setIsValuesChanged(true)
          }}
        >
          <Flex vertical gap={'middle'}>

            <Flex align={'center'} gap={'small'}>
              <SwitchGreen
                size={'small'}
                value={isUploadByFile}
                onChange={(value) => setIsUploadByFile(value)}
                disabled={isLoading}
              />
              <span>{t('factors.byFile')}</span>
            </Flex>

            {!isUploadByFile &&
              <Flex vertical gap={'middle'}>
                <Form.Item
                  name="json"
                  rules={[{required: true, message: t('factors.engineEmpty')}]}
                >
                  <TextArea disabled={isLoading}/>
                </Form.Item>
              </Flex>
            }

            {isUploadByFile &&
              <Flex vertical gap={'middle'}>
                <Form.Item
                  name="file"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                >
                  <Upload
                    listType="picture"
                    accept=".json"
                    onRemove={(file: UploadFile) => {
                      const index = fileList.indexOf(file);
                      const newFileList = fileList.slice();
                      newFileList.splice(index, 1);
                      setFileList(newFileList);
                    }}
                    beforeUpload={(file) => {
                      setFileList([...fileList, file]);
                      return false
                    }}
                    disabled={isLoading}
                  >
                    {fileList.length === 0 &&
                      <Button icon={<IconUploadFile/>} size={'large'} disabled={isLoading}
                              style={{width: '100%'}}>{t('dataset.uploadFile')}</Button>
                    }

                  </Upload>
                </Form.Item>
              </Flex>
            }
          </Flex>
        </Form>
      </Flex>
    </Card>
  )
}
